import React from 'react';
import { Scroll, Users, Heart, Calendar, Star, BookOpen } from 'lucide-react';

const KaupapaHistory: React.FC = () => {
  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold text-center text-ocean-deep mb-2">
        Te Tīmatanga o te Kaupapa
      </h1>
      <p className="text-xl text-center text-gray-700 mb-8">
        The Beginning of Our Journey
      </p>

      {/* Introduction Section */}
      <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg p-8 mb-8">
        <div className="flex items-center mb-4">
          <Scroll className="w-8 h-8 text-ocean-teal mr-3" />
          <h2 className="text-2xl font-bold text-ocean-deep">Our Story</h2>
        </div>
        <p className="text-gray-700 leading-relaxed mb-4">
          This platform represents a decade-long journey of asserting our sovereignty and customary rights 
          based on He Whakaputanga. What began as a vision to preserve and protect our whakapapa has grown 
          into a comprehensive platform serving our whānau, hapū, and iwi.
        </p>
        <p className="text-gray-700 leading-relaxed">
          This page honors those who walked this path before us, those who stand with us today, and the 
          generations who will carry this kaupapa forward.
        </p>
      </div>

      {/* Timeline Section */}
      <div className="bg-white rounded-lg shadow-md p-8 mb-8">
        <div className="flex items-center mb-6">
          <Calendar className="w-8 h-8 text-ocean-teal mr-3" />
          <h2 className="text-2xl font-bold text-ocean-deep">The Journey: 10 Years of Sovereignty</h2>
        </div>
        
        <div className="space-y-6">
          <div className="border-l-4 border-ocean-teal pl-6">
            <h3 className="font-bold text-lg text-ocean-deep">The Beginning</h3>
            <p className="text-gray-700 mt-2">
              Our journey began with a simple yet powerful vision: to assert our sovereignty through 
              He Whakaputanga and protect our customary rights for future generations.
            </p>
          </div>

          <div className="border-l-4 border-sea-green pl-6">
            <h3 className="font-bold text-lg text-ocean-deep">Building the Foundation</h3>
            <p className="text-gray-700 mt-2">
              Years of research, wānanga, and collaboration with kaumātua and legal experts to establish 
              a solid foundation for our claims and rights.
            </p>
          </div>

          <div className="border-l-4 border-ocean-blue pl-6">
            <h3 className="font-bold text-lg text-ocean-deep">Digital Transformation</h3>
            <p className="text-gray-700 mt-2">
              The creation of this platform to preserve our whakapapa digitally and make our knowledge 
              accessible to all whānau members, wherever they may be.
            </p>
          </div>
        </div>
      </div>

      {/* People Recognition Section */}
      <div className="bg-white rounded-lg shadow-md p-8 mb-8">
        <div className="flex items-center mb-6">
          <Users className="w-8 h-8 text-ocean-teal mr-3" />
          <h2 className="text-2xl font-bold text-ocean-deep">Ngā Tangata - The People</h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-teal-50 rounded-lg p-6">
            <div className="flex items-center mb-3">
              <Star className="w-6 h-6 text-yellow-600 mr-2" />
              <h3 className="text-lg font-bold text-ocean-deep">Our Founders</h3>
            </div>
            <p className="text-gray-700">
              Those visionaries who saw the need and took the first brave steps on this journey.
            </p>
            <ul className="mt-3 space-y-2 text-gray-700">
              <li>• Rutene - Visionary and keeper of knowledge</li>
              <li>• [Names to be added by whānau]</li>
            </ul>
          </div>

          <div className="bg-emerald-50 rounded-lg p-6">
            <div className="flex items-center mb-3">
              <Heart className="w-6 h-6 text-sea-green mr-2" />
              <h3 className="text-lg font-bold text-ocean-deep">Our Supporters</h3>
            </div>
            <p className="text-gray-700">
              The dedicated whānau who have supported and sustained this kaupapa through the years.
            </p>
            <ul className="mt-3 space-y-2 text-gray-700">
              <li>• Legal advisors and researchers</li>
              <li>• Technical developers and designers</li>
              <li>• Kaumātua and cultural advisors</li>
            </ul>
          </div>
        </div>

        <div className="mt-6 bg-gradient-to-r from-teal-100 to-emerald-100 rounded-lg p-6">
          <h3 className="text-lg font-bold text-ocean-deep mb-3">Those Who Came Before</h3>
          <p className="text-gray-700">
            We honor our tīpuna who preserved our whakapapa and fought for our rights long before 
            digital platforms existed. Their wisdom and courage guide us still.
          </p>
        </div>
      </div>

      {/* He Whakaputanga Section */}
      <div className="bg-white rounded-lg shadow-md p-8 mb-8">
        <div className="flex items-center mb-6">
          <BookOpen className="w-8 h-8 text-ocean-teal mr-3" />
          <h2 className="text-2xl font-bold text-ocean-deep">He Whakaputanga - Our Foundation</h2>
        </div>
        
        <p className="text-gray-700 leading-relaxed mb-4">
          He Whakaputanga o te Rangatiratanga o Nu Tireni (Declaration of Independence) signed in 1835 
          remains the foundation of our sovereignty. This declaration, signed by our rangatira, affirmed 
          our authority over our lands and people.
        </p>
        
        <p className="text-gray-700 leading-relaxed">
          From this declaration flows our customary rights, our tino rangatiratanga, and our responsibility 
          to protect and preserve our taonga for future generations. This platform is a modern expression 
          of these ancient rights and responsibilities.
        </p>
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-r from-ocean-deep to-sea-green text-white rounded-lg p-8 text-center">
        <h2 className="text-2xl font-bold mb-4">Continue the Journey With Us</h2>
        <p className="text-lg mb-6">
          This kaupapa belongs to all of us. Your stories, your knowledge, and your participation 
          strengthen our collective voice and preserve our heritage.
        </p>
        <p className="text-sm italic">
          "He aha te mea nui o te ao? He tangata, he tangata, he tangata"
        </p>
        <p className="text-sm mt-2">
          What is the most important thing in the world? It is people, it is people, it is people
        </p>
      </div>
    </div>
  );
};

export default KaupapaHistory;